// Tap-to-unlock with multi-key support.
//
// Envelope scheme: one random "data key" (DK) per browser wraps every room /
// transcripts PSK (per-scope subkeys via HKDF). The DK itself is wrapped once
// under each enrolled security key's PRF secret. So:
//   - unlock  = tap ANY enrolled key -> unwrap DK -> unwrap the scope's PSK
//   - add key = tap an existing key (get DK) + enroll the new one (wrap DK)
//   - any of N keys unlocks all scopes; no PSK stored in the clear anywhere.
//
// Nothing usable is on disk without a key: DK exists only in memory (transiently)
// or wrapped under a key's PRF secret; PSKs are wrapped under DK.

import { startRegistration } from "@simplewebauthn/browser";
import { api } from "./api";

const KEYS = "wn_keys"; // JSON EnrolledKey[]
const DK_PREFIX = "wn_dk_"; // + keyId  -> {iv,ct}: DK wrapped under the key's secret
const WRAP_PREFIX = "wn_wrapped_"; // + scope -> {iv,ct}: PSK wrapped under DK
const LEGACY_METHOD = "wn_unlock_method"; // single-key scheme, pre-multi-key
const PRF_SALT = new Uint8Array(new TextEncoder().encode("whitenoise/prf/v1//////////////00"));

type KeyKind = "webauthn" | "software";
export interface EnrolledKey {
  id: string;
  kind: KeyKind;
  label: string;
  credentialId?: string; // webauthn
  seed?: string; // software test key
}
interface Wrapped {
  iv: string;
  ct: string;
}

// --- helpers -------------------------------------------------------------
function b64(bytes: ArrayBuffer | Uint8Array): string {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
  return btoa(s);
}
function unb64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function rand(n: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(n));
}
function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(s));
}
// Credential ids from the server are base64url; convert to/from that form.
function b64url(bytes: ArrayBuffer | Uint8Array): string {
  return b64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  let t = s.replace(/-/g, "+").replace(/_/g, "/");
  while (t.length % 4) t += "=";
  return unb64(t);
}

export function webauthnSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

// One-time migration: the old single-key scheme is incompatible with the DK
// envelope, so drop it (and its now-orphaned wraps) and let the user re-enroll.
function migrate(): void {
  if (localStorage.getItem(KEYS)) return;
  if (localStorage.getItem(LEGACY_METHOD)) {
    localStorage.removeItem(LEGACY_METHOD);
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(WRAP_PREFIX)) localStorage.removeItem(k);
    }
  }
}

// --- enrolled keys -------------------------------------------------------
export function listKeys(): EnrolledKey[] {
  migrate();
  try {
    return JSON.parse(localStorage.getItem(KEYS) ?? "[]") as EnrolledKey[];
  } catch {
    return [];
  }
}
function saveKeys(keys: EnrolledKey[]): void {
  localStorage.setItem(KEYS, JSON.stringify(keys));
}
export function hasKeys(): boolean {
  return listKeys().length > 0;
}

// Back-compat shims used by the room/transcripts unlock screens.
export function getEnrolledMethod(): EnrolledKey | null {
  return listKeys()[0] ?? null;
}
export function methodLabel(): string | null {
  const n = listKeys().length;
  if (!n) return null;
  return n === 1 ? "🔑 Tap key" : `🔑 Tap (any of ${n} keys)`;
}

// --- key secrets (the tap) ----------------------------------------------
async function softwareSecret(seed: string): Promise<Uint8Array<ArrayBuffer>> {
  const key = await crypto.subtle.importKey("raw", unb64(seed), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return new Uint8Array(await crypto.subtle.sign("HMAC", key, PRF_SALT));
}

function prfFrom(cred: PublicKeyCredential): Uint8Array<ArrayBuffer> | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const secret: ArrayBuffer | undefined = (cred.getClientExtensionResults() as any)?.prf?.results?.first;
  return secret ? new Uint8Array(secret) : null;
}

// Tap a specific webauthn key and return its PRF secret.
async function webauthnSecret(credentialId: string): Promise<Uint8Array<ArrayBuffer>> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: rand(32),
      allowCredentials: [{ type: "public-key", id: b64urlToBytes(credentialId) }],
      userVerification: "discouraged",
      timeout: 60_000,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      extensions: { prf: { eval: { first: PRF_SALT } } } as any,
    },
  })) as PublicKeyCredential | null;
  if (!assertion) throw new Error("tap cancelled");
  const secret = prfFrom(assertion);
  if (!secret) throw new Error("this key/browser didn't return a PRF secret (need Chrome/Edge + a PRF-capable key)");
  return secret;
}

// Tap ANY enrolled key and return which key responded plus its secret.
async function tapAny(): Promise<{ key: EnrolledKey; secret: Uint8Array<ArrayBuffer> }> {
  const keys = listKeys();
  const webauthnKeys = keys.filter((k) => k.kind === "webauthn");
  if (webauthnKeys.length > 0) {
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: rand(32),
        allowCredentials: webauthnKeys.map((k) => ({ type: "public-key" as const, id: b64urlToBytes(k.credentialId!) })),
        userVerification: "discouraged",
        timeout: 60_000,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        extensions: { prf: { eval: { first: PRF_SALT } } } as any,
      },
    })) as PublicKeyCredential | null;
    if (!assertion) throw new Error("tap cancelled");
    const respId = b64url(assertion.rawId);
    const key = webauthnKeys.find((k) => k.credentialId === respId);
    if (!key) throw new Error("tapped an unrecognized key");
    const secret = prfFrom(assertion);
    if (!secret) throw new Error("this key/browser didn't return a PRF secret (need Chrome/Edge + a PRF-capable key)");
    return { key, secret };
  }
  const sw = keys.find((k) => k.kind === "software");
  if (sw) return { key: sw, secret: await softwareSecret(sw.seed!) };
  throw new Error("no unlock key enrolled");
}

// --- data key (DK) envelope ---------------------------------------------
async function aesFromSecret(secret: Uint8Array<ArrayBuffer>, info: string): Promise<CryptoKey> {
  const hk = await crypto.subtle.importKey("raw", secret, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: utf8(info) },
    hk,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function wrapDK(keyId: string, secret: Uint8Array<ArrayBuffer>, dk: Uint8Array<ArrayBuffer>): Promise<void> {
  const key = await aesFromSecret(secret, "wn-dk");
  const iv = rand(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, dk);
  localStorage.setItem(DK_PREFIX + keyId, JSON.stringify({ iv: b64(iv), ct: b64(new Uint8Array(ct)) }));
}

async function unwrapDK(keyId: string, secret: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const raw = localStorage.getItem(DK_PREFIX + keyId);
  if (!raw) throw new Error("this key isn't set up on this browser");
  const { iv, ct } = JSON.parse(raw) as Wrapped;
  const key = await aesFromSecret(secret, "wn-dk");
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(iv) }, key, unb64(ct)));
}

// Tap any key -> recover the browser's data key (held only in memory).
async function getDataKey(): Promise<Uint8Array<ArrayBuffer>> {
  const { key, secret } = await tapAny();
  return unwrapDK(key.id, secret);
}

async function scopeKey(dk: Uint8Array<ArrayBuffer>, scope: string): Promise<CryptoKey> {
  return aesFromSecret(dk, "wn-wrap:" + scope);
}

// --- enroll / remove -----------------------------------------------------
// Enroll a physical security key. Excludes already-enrolled keys so the same
// device isn't registered twice.
export async function enrollWebauthn(username: string, label = "Security key"): Promise<void> {
  void username; // username is taken from the authenticated session server-side
  const existing = listKeys();
  // Recover (or mint) the browser data key BEFORE creating the new credential.
  const dk = existing.length === 0 ? rand(32) : await getDataKey();

  // Registration ceremony via the server, so this credential ALSO works for
  // tap-to-login. The server options enable the PRF extension.
  const { options } = await api.webauthnRegisterOptions();
  const attResp = await startRegistration({ optionsJSON: options });
  const { credentialId } = await api.webauthnRegisterVerify(attResp, label);

  // Derive the PRF wrapping secret via a get() (consistent enroll<->unlock) and
  // wrap the data key so this credential can also unlock content by tap.
  const secret = await webauthnSecret(credentialId);
  const key: EnrolledKey = { id: crypto.randomUUID(), kind: "webauthn", label, credentialId };
  await wrapDK(key.id, secret, dk);
  saveKeys([...existing, key]);
}

// Enroll a local software "key" (test only — not hardware-backed).
export async function enrollSoftware(label = "Software test key"): Promise<void> {
  const existing = listKeys();
  const dk = existing.length === 0 ? rand(32) : await getDataKey();
  const key: EnrolledKey = { id: crypto.randomUUID(), kind: "software", label, seed: b64(rand(32)) };
  await wrapDK(key.id, await softwareSecret(key.seed!), dk);
  saveKeys([...existing, key]);
}

export function renameKey(keyId: string, label: string): void {
  saveKeys(listKeys().map((k) => (k.id === keyId ? { ...k, label } : k)));
}

export function removeKey(keyId: string): void {
  const keys = listKeys().filter((k) => k.id !== keyId);
  localStorage.removeItem(DK_PREFIX + keyId);
  saveKeys(keys);
  if (keys.length === 0) {
    // No keys left -> DK (and thus every wrapped PSK) is unrecoverable; clean up.
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(WRAP_PREFIX)) localStorage.removeItem(k);
    }
  }
}

// --- per-scope PSK wrap / unwrap -----------------------------------------
export function hasWrapped(scope: string): boolean {
  return localStorage.getItem(WRAP_PREFIX + scope) !== null;
}

export async function rememberPsk(scope: string, psk: string): Promise<void> {
  const dk = await getDataKey();
  const key = await scopeKey(dk, scope);
  const iv = rand(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, utf8(psk));
  localStorage.setItem(WRAP_PREFIX + scope, JSON.stringify({ iv: b64(iv), ct: b64(new Uint8Array(ct)) }));
}

export async function tapUnlock(scope: string): Promise<string> {
  const raw = localStorage.getItem(WRAP_PREFIX + scope);
  if (!raw) throw new Error("nothing wrapped for this scope yet");
  const { iv, ct } = JSON.parse(raw) as Wrapped;
  const dk = await getDataKey();
  const key = await scopeKey(dk, scope);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(iv) }, key, unb64(ct));
  return new TextDecoder().decode(pt);
}
